/**
 * The three-step checkout on each application lane (LG-1033), as pure
 * state: which steps count as finished and how far the applicant may open.
 *
 * Both lanes share the shape — video, CV, then the thing that ends the
 * lane (payment or a booking) — and differ only in whether the video may
 * be skipped. Keeping that rule here, and not in the markup, is what makes
 * "Hold your spot cannot reach payment without a recording" a fact rather
 * than a hidden button.
 */

/** Hold your spot = pay now; Talk to us first = book a call, pay later. */
export type Lane = 'hold' | 'talk'

/** 0 = video, 1 = CV, 2 = payment or booking. */
export type StepIndex = 0 | 1 | 2

/**
 * The steps that collect an artefact — everything before the step that
 * commits the applicant. Listed rather than derived from `StepIndex`: the
 * companion maps below are keyed by this type, so a new artefact step is a
 * compile error at each map instead of silently reading the wrong answer.
 */
export type ArtefactStep = 0 | 1

/** What the applicant did with an optional artefact. */
export type StepAnswer = 'pending' | 'attached' | 'skipped'

export interface LaneAnswers {
  video: StepAnswer
  cv: StepAnswer
}

// `as const satisfies` rather than `Readonly<LaneAnswers>`: readonly property
// modifiers do not affect assignability, so the annotation promised a
// protection TypeScript was not giving. The literal types do make a stray
// write an error at the source.
export const EMPTY_LANE_ANSWERS = {
  video: 'pending',
  cv: 'pending',
} as const satisfies LaneAnswers

export const LANE_TITLE: Record<Lane, string> = {
  hold: 'Hold your spot',
  talk: 'Talk to us first',
}

/**
 * Only "Talk to us first" may skip the video. On the paid lane the deposit
 * holds the place and the recording is what gets reviewed (Jon + Mike,
 * 2026-09-02); on the call lane the call does that job instead.
 */
export function videoSkippable(lane: Lane): boolean {
  return lane === 'talk'
}

/** Which answer each artefact step collects. */
const ANSWER_FIELD: Record<ArtefactStep, keyof LaneAnswers> = {
  0: 'video',
  1: 'cv',
}

/**
 * When a skip counts as finishing the step, per lane. The value is the whole
 * rule for its step — `stepDone` adds no lane logic of its own — so a step
 * that may never be skipped is written `() => false` and stays unskippable.
 */
const SKIP_FINISHES: Record<ArtefactStep, (lane: Lane) => boolean> = {
  0: videoSkippable,
  1: () => true,
}

/** Whether an artefact step is finished — a refused skip does not count. */
export function stepDone(
  lane: Lane,
  step: ArtefactStep,
  answers: LaneAnswers,
): boolean {
  const answer = answers[ANSWER_FIELD[step]]
  if (answer === 'attached') return true
  if (answer === 'skipped') return SKIP_FINISHES[step](lane)
  return false
}

/**
 * The furthest step the applicant may open. An unreached step cannot be
 * opened, so the payment form is unreachable without a recording on the
 * paid lane no matter what the markup offers.
 */
export function reachableStep(lane: Lane, answers: LaneAnswers): StepIndex {
  if (!stepDone(lane, 0, answers)) return 0
  if (!stepDone(lane, 1, answers)) return 1
  return 2
}

/**
 * The pasted video link, normalised, or null when it is not a web address.
 * The fallback for a phone whose browser cannot run the recorder, so it
 * accepts any http(s) URL rather than guessing which hosts people use.
 */
export function parseVideoLink(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
  return url.toString()
}
